import { ParseError } from './parse-error'

// ---------------------------------------------------------------------------
// mulDiv — the one rounding rule (spec §1.5): a·num/den, half away from zero.
// All operands are integers (cents, basis points); |a·num|·2 + den must stay a safe integer.
// ---------------------------------------------------------------------------
export function mulDiv(a: number, num: number, den: number): number {
  if (!Number.isInteger(a) || !Number.isInteger(num) || !Number.isInteger(den)) {
    throw new RangeError(`mulDiv expects integers, got ${a}, ${num}, ${den}`)
  }
  if (den <= 0) throw new RangeError(`mulDiv expects a positive denominator, got ${den}`)
  const product = a * num
  const magnitude = Math.abs(product) * 2 + den
  if (!Number.isSafeInteger(magnitude)) throw new RangeError(`mulDiv overflow: ${a} * ${num}`)
  const quotient = Math.floor(magnitude / (2 * den))
  if (quotient === 0) return 0 // never return -0
  return product < 0 ? -quotient : quotient
}

// ---------------------------------------------------------------------------
// parseMoneyCents — strict HighLevel money grammar (spec §1.6). No v25 toNumber_ heuristic.
// ---------------------------------------------------------------------------
const GRAMMAR = {
  'en-US': { grouped: /^\d{1,3}(,\d{3})*(\.\d{1,2})?$/, plain: /^\d+(\.\d{1,2})?$/, group: ',', decimal: '.' },
  'de-DE': { grouped: /^\d{1,3}(\.\d{3})*(,\d{1,2})?$/, plain: /^\d+(,\d{1,2})?$/, group: '.', decimal: ',' },
} as const

export function parseMoneyCents(raw: unknown, format: 'en-US' | 'de-DE'): number | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) throw new ParseError(`money: not a finite number: ${raw}`)
    return Math.round(raw * 100)
  }
  if (typeof raw !== 'string') throw new ParseError(`money: unsupported value type ${typeof raw}`)
  const s = raw.replace(/[€\s ]/g, '')
  if (s === '' || s === '-') return null
  const g = GRAMMAR[format]
  if (!g.grouped.test(s) && !g.plain.test(s)) {
    throw new ParseError(`money: "${raw}" is not a valid ${format} amount`)
  }
  const parts = s.split(g.decimal)
  const intPart = (parts[0] ?? '').split(g.group).join('')
  const fracPart = ((parts[1] ?? '') + '00').slice(0, 2)
  return Number(intPart) * 100 + Number(fracPart)
}

// ---------------------------------------------------------------------------
// parsePercentBp — v25 toPercent_ semantics (rulecheck §1.1): a value > 1 or a '%' sign means percent.
// "2,5%", "2.5", 0.025, 5 → 250 / 250 / 250 / 500 basis points.
// ---------------------------------------------------------------------------
export function parsePercentBp(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null
  let value: number
  let hasPercentSign = false
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) throw new ParseError(`percent: not a finite number: ${raw}`)
    value = raw
  } else if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (trimmed === '' || trimmed === '-') return null
    hasPercentSign = trimmed.includes('%')
    let s = trimmed.replace(/[%€\s ]/g, '')
    if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.')
    else if (s.includes(',')) s = s.replace(',', '.')
    if (!/^-?(\d+(\.\d+)?|\.\d+)$/.test(s)) throw new ParseError(`percent: "${raw}" is not a number`)
    value = Number(s)
  } else {
    throw new ParseError(`percent: unsupported value type ${typeof raw}`)
  }
  const isPercentScale = hasPercentSign || value > 1
  return isPercentScale ? Math.round(value * 100) : Math.round(value * 10_000)
}

// ---------------------------------------------------------------------------
// formatEur — '4.500,00 €' (Intl de-DE; the NBSP Intl puts before € is replaced by a plain space).
// ---------------------------------------------------------------------------
const EUR_FORMAT = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export function formatEur(cents: number): string {
  if (!Number.isInteger(cents)) throw new RangeError(`formatEur expects integer cents, got ${cents}`)
  return EUR_FORMAT.format(cents / 100).replace(/ /g, ' ')
}
